import { GlobeAltIcon } from "@heroicons/react/outline";
import momentTimezone from "moment-timezone";
import { useMemo } from "react";

import { SearchableDropdown } from "@/components/ui/searchable-dropdown";
import { useTimezone, type TimezoneValue } from "@/context/timezone-context";
import { cn } from "@/lib/utils";

interface TimezoneItem {
  id: string;
  label: string;
  description?: string;
  rightLabel?: string;
  searchKeywords?: string;
  value: TimezoneValue;
}

const regionNames = new Intl.DisplayNames(["en"], { type: "region" });
const US_CANADA_COUNTRY_CODES = new Set(["US", "CA"]);

const timezoneCountryByName = momentTimezone.tz
  .countries()
  .reduce((countryByTimezone, countryCode) => {
    momentTimezone.tz.zonesForCountry(countryCode, false).forEach(timezoneName => {
      if (!countryByTimezone.has(timezoneName)) {
        countryByTimezone.set(timezoneName, countryCode);
      }
    });

    return countryByTimezone;
  }, new Map<string, string>());

const getCurrentBrowserTimezone = (): string => momentTimezone.tz.guess();

const formatOffsetLabel = (ianaName: string): string => {
  const offset = momentTimezone.tz(ianaName).format("Z");
  return offset === "+00:00" ? "UTC" : `UTC${offset}`;
};

const formatAbbreviation = (ianaName: string): string => momentTimezone.tz(ianaName).format("z");

const formatDisplayAbbreviation = (ianaName: string): string | undefined => {
  const abbreviation = formatAbbreviation(ianaName);
  return /^[+-]\d{1,2}$/.test(abbreviation) ? undefined : abbreviation;
};

const formatCountryLabel = (ianaName: string): string | undefined => {
  const countryCode = timezoneCountryByName.get(ianaName);
  return countryCode ? regionNames.of(countryCode) : undefined;
};

const isUsCanadaTimezone = (ianaName: string): boolean =>
  US_CANADA_COUNTRY_CODES.has(timezoneCountryByName.get(ianaName) ?? "");

const formatTimezoneLabel = (ianaName: string): string =>
  ianaName
    .split("/")
    .slice(1)
    .map(part => part.replace(/_/g, " "))
    .join(", ");

const formatTimezoneDescription = (ianaName: string): string => {
  const countryLabel = formatCountryLabel(ianaName);
  const abbreviation = formatDisplayAbbreviation(ianaName);

  return [countryLabel, abbreviation].filter(Boolean).join(", ");
};

const buildTimezoneItem = (ianaName: string): TimezoneItem => ({
  id: ianaName,
  label: formatTimezoneLabel(ianaName),
  description: formatTimezoneDescription(ianaName),
  rightLabel: formatOffsetLabel(ianaName),
  searchKeywords: ianaName.replace(/_/g, " "),
  value: ianaName,
});

const buildNorthAmericaTimezoneItem = ({
  label,
  value,
  regionLabel = "US & Canada",
  searchKeywords,
}: {
  label: string;
  value: string;
  regionLabel?: string;
  searchKeywords: string;
}): TimezoneItem => ({
  id: value,
  label,
  description: `${regionLabel}, ${formatDisplayAbbreviation(value) ?? formatAbbreviation(value)}`,
  rightLabel: formatOffsetLabel(value),
  searchKeywords,
  value,
});

const browserTimezone = getCurrentBrowserTimezone();

const PINNED: TimezoneItem[] = [
  {
    id: "local",
    label: "Local time",
    description: formatTimezoneDescription(browserTimezone),
    rightLabel: formatOffsetLabel(browserTimezone),
    value: "local",
  },
  {
    id: "UTC",
    label: "Coordinated Universal Time",
    description: "UTC, GMT",
    rightLabel: "UTC",
    value: "UTC",
  },
];

const US_CANADA_TIMEZONE_ITEMS: TimezoneItem[] = [
  buildNorthAmericaTimezoneItem({
    label: "Eastern Time",
    value: "America/New_York",
    searchKeywords: "EST EDT New York Toronto Montreal Boston Washington Miami Atlanta",
  }),
  buildNorthAmericaTimezoneItem({
    label: "Central Time",
    value: "America/Chicago",
    searchKeywords: "CST CDT Chicago Dallas Houston Winnipeg",
  }),
  buildNorthAmericaTimezoneItem({
    label: "Mountain Time",
    value: "America/Denver",
    searchKeywords: "MST MDT Denver Calgary Edmonton Salt Lake City",
  }),
  buildNorthAmericaTimezoneItem({
    label: "Arizona Time",
    value: "America/Phoenix",
    regionLabel: "US",
    searchKeywords: "Arizona Phoenix MST no daylight saving",
  }),
  buildNorthAmericaTimezoneItem({
    label: "Pacific Time",
    value: "America/Los_Angeles",
    searchKeywords: "PST PDT Los Angeles San Francisco Seattle Vancouver",
  }),
  buildNorthAmericaTimezoneItem({
    label: "Alaska Time",
    value: "America/Anchorage",
    regionLabel: "US",
    searchKeywords: "AKST AKDT Alaska Anchorage",
  }),
  buildNorthAmericaTimezoneItem({
    label: "Hawaii Time",
    value: "Pacific/Honolulu",
    regionLabel: "US",
    searchKeywords: "Hawaii Honolulu HST",
  }),
  buildNorthAmericaTimezoneItem({
    label: "Atlantic Time",
    value: "America/Halifax",
    regionLabel: "Canada",
    searchKeywords: "AST ADT Halifax Nova Scotia New Brunswick",
  }),
  buildNorthAmericaTimezoneItem({
    label: "Newfoundland Time",
    value: "America/St_Johns",
    regionLabel: "Canada",
    searchKeywords: "NST NDT Newfoundland St Johns Labrador",
  }),
];

/** Full IANA zone list from moment-timezone, excluding Etc/ aliases. */
const IANA_ITEMS: TimezoneItem[] = momentTimezone.tz
  .names()
  .filter(name => !name.startsWith("Etc/") && name.includes("/"))
  .map(buildTimezoneItem)
  .sort((a, b) => a.label.localeCompare(b.label));

const IANA_REGIONS = [
  "Africa",
  "America",
  "Antarctica",
  "Arctic",
  "Asia",
  "Atlantic",
  "Australia",
  "Europe",
  "Indian",
  "Pacific",
];

const IANA_CATEGORIES = IANA_REGIONS.filter(region => region !== "Europe")
  .map(region => ({
    label: region,
    items: IANA_ITEMS.filter(
      item => item.id.startsWith(`${region}/`) && !isUsCanadaTimezone(item.id),
    ),
  }))
  .filter(category => category.items.length > 0);

const CATEGORIES: { label: string; items: TimezoneItem[] }[] = [
  { label: "Common", items: PINNED },
  { label: "US & Canada", items: US_CANADA_TIMEZONE_ITEMS },
  { label: "Europe", items: IANA_ITEMS.filter(item => item.id.startsWith("Europe/")) },
  ...IANA_CATEGORIES,
];

const getCuratedItem = (tz: TimezoneValue): TimezoneItem | undefined =>
  [...PINNED, ...US_CANADA_TIMEZONE_ITEMS].find(item => item.value === tz);

const getShortLabel = (tz: TimezoneValue): string => {
  if (tz === "local") {
    return "Local";
  }
  if (tz === "UTC") {
    return "UTC";
  }

  const curatedItem = getCuratedItem(tz);
  if (curatedItem) {
    return curatedItem.label;
  }

  const parts = tz.split("/");
  return parts[parts.length - 1].replace(/_/g, " ");
};

export const TimezoneSelector = ({ triggerClassName }: { triggerClassName?: string }) => {
  const { timezone, setTimezone } = useTimezone();

  const selectedItem = useMemo<TimezoneItem>(() => {
    const curatedItem = getCuratedItem(timezone);
    if (curatedItem) {
      return curatedItem;
    }

    return buildTimezoneItem(timezone);
  }, [timezone]);

  const customTrigger = (
    <div
      className={cn(
        "flex h-10 items-center gap-2 rounded-md border border-input bg-transparent px-3 text-sm font-normal shadow-sm hover:bg-accent",
        triggerClassName,
      )}
      title="Change timezone for timestamps"
    >
      <GlobeAltIcon className="h-4 w-4 flex-shrink-0 text-gray-500" />
      <span className="max-w-[110px] truncate">{getShortLabel(timezone)}</span>
    </div>
  );

  return (
    <SearchableDropdown
      categories={CATEGORIES}
      selectedItem={selectedItem}
      customTrigger={customTrigger}
      triggerClassName="w-auto shrink-0"
      searchPlaceholder="Search by country, city, abbreviation..."
      onSelect={(item: TimezoneItem | undefined) => {
        if (item) {
          setTimezone(item.value);
        }
      }}
      className="w-[26rem]"
    />
  );
};
